<script setup lang="ts">
import type { ErrorDto, LowestFare, LowestFareFilterType } from '@optionway/models';
import { ArrowLeft, ArrowRight, Calendar, ChevronRight, FilterX, Globe, MapPinned, Plane } from 'lucide-vue-next';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

const wheelGesturesPlugin = WheelGesturesPlugin();

const { $appConfig } = useNuxtApp();
const { locale } = useI18n();
const localePath = useLocalePath();
const fullLocale = useFullLocale();
const apiUrl = $appConfig?.api?.url;
const locationLabels = useLocationLabels();
const searchCriteria = useSearchCriteriaStore();
const gtmStore = useGtmStore();
const partnerId = usePartnerId();
// Captured at setup (undefined on the client): the SSR fallback below marks the render degraded.
const requestEvent = useRequestEvent();
const emit = defineEmits<{ 'change-origin': [] }>();

const prefix = 'components.home.lowestFares';

const activeFilter = useState<LowestFareFilterType | undefined>('lowest-fare-active-filter');
const setFilter = (filter: LowestFareFilterType) => {
  activeFilter.value = activeFilter.value === filter ? undefined : filter;
};
const clearFilter = () => {
  activeFilter.value = undefined;
};

const { data: lowestFares, status } = useQuery<LowestFare[], ErrorDto>({
  key: () => [
    'lowest-fares',
    partnerId.value,
    locale.value,
    searchCriteria.originCodeWithDefault,
    activeFilter.value ?? '',
  ],
  query: () => {
    const filterPath = activeFilter.value ? `/${activeFilter.value}` : '';
    // Degrades to the section's empty state when the BFF is unavailable (visibleFares/isEmpty
    // already cover it): an SSR-blocking query that rejects fails the whole home render with a 500.
    return $fetch<LowestFare[]>(
      `${apiUrl}/home/lowest-fares/${partnerId.value}/${locale.value}/${searchCriteria.originCodeWithDefault}${filterPath}`,
    ).catch((): LowestFare[] => {
      markRenderDegraded(requestEvent);
      return [];
    });
  },
  enabled: () => !!apiUrl,
  staleTime: 1000 * 60 * 30,
});

// One dead picture must not blank the other cards, so failures are tracked per destination.
const failedPictures = ref(new Set<string>());

const visibleFares = computed(() => lowestFares.value ?? []);
const isEmpty = computed(() => status.value !== 'pending' && visibleFares.value.length === 0);
const isEmptyDueToFilter = computed(() => isEmpty.value && activeFilter.value !== undefined);

const currentSlideIndex = ref(0);
const onCarouselInit = (
  api: { selectedScrollSnap: () => number; on: (event: 'select', handler: () => void) => void } | undefined,
) => {
  if (!api) return;
  currentSlideIndex.value = 0;
  api.on('select', () => {
    currentSlideIndex.value = api.selectedScrollSnap();
  });
};

const scrollToOrigin = () => {
  // Imperative one-shot in a client-only click path: useWindowScroll attached a permanent scroll
  // listener (throttle 0, idle timers) for this single write, with no SSR risk to guard against.
  globalThis.scrollTo({ top: 0, behavior: 'smooth' });
  emit('change-origin');
};
</script>

<template>
  <div class="flex flex-col items-center gap-6">
    <div class="flex flex-col items-center gap-6 text-center">
      <h2 class="flex flex-col items-center gap-1 text-3xl font-semibold leading-tight text-text-primary-900">
        <span>{{ $t(`${prefix}.titleStart`) }}</span>
        <span class="flex flex-wrap items-center justify-center gap-2">
          {{ $t(`${prefix}.fromCity`) }}
          <button
            type="button"
            class="inline-flex items-center gap-1 overflow-hidden rounded-lg border border-brand-secondary-500 bg-background px-3 py-0.5 text-lg font-semibold text-brand-secondary-700 shadow-sm transition-colors hover:bg-brand-secondary-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-secondary-700"
            @click="scrollToOrigin"
          >
            <MapPinned
              class="size-4 shrink-0"
              aria-hidden="true"
            />
            <Transition
              mode="out-in"
              enter-active-class="transition-all duration-200 ease-out"
              enter-from-class="opacity-0 translate-y-3"
              leave-active-class="transition-all duration-200 ease-in"
              leave-to-class="opacity-0 -translate-y-3"
            >
              <span
                :key="searchCriteria.originCodeWithDefault"
                class="inline-block"
                >{{ locationLabels.city(searchCriteria.originCodeWithDefault) }}</span
              >
            </Transition>
            <ChevronRight
              class="size-4 shrink-0"
              :stroke-width="2.5"
              aria-hidden="true"
            />
          </button>
        </span>
      </h2>
      <p class="text-sm font-normal text-text-secondary-600">{{ $t(`${prefix}.tagline`) }}</p>
    </div>

    <div
      role="radiogroup"
      :aria-label="$t(`${prefix}.filtersLabel`)"
      class="flex flex-wrap items-center justify-center gap-3"
    >
      <UiButton
        role="radio"
        :aria-checked="activeFilter === undefined"
        :variant="activeFilter === undefined ? 'primary' : 'secondary'"
        :color="activeFilter === undefined ? 'brand-primary' : 'gray'"
        size="xs"
        shape="full"
        @click="clearFilter()"
      >
        <Globe
          class="shrink-0"
          aria-hidden="true"
        />
        {{ $t(`${prefix}.filters.all`) }}
      </UiButton>
      <UiButton
        role="radio"
        :aria-checked="activeFilter === 'DIRECT'"
        :variant="activeFilter === 'DIRECT' ? 'primary' : 'secondary'"
        :color="activeFilter === 'DIRECT' ? 'brand-primary' : 'gray'"
        size="xs"
        shape="full"
        :disabled="isEmpty && !isEmptyDueToFilter"
        @click="setFilter('DIRECT')"
      >
        <Plane
          class="shrink-0"
          aria-hidden="true"
        />
        {{ $t(`${prefix}.filters.direct`) }}
      </UiButton>
      <UiButton
        role="radio"
        :aria-checked="activeFilter === 'SHORT_STAY'"
        :variant="activeFilter === 'SHORT_STAY' ? 'primary' : 'secondary'"
        :color="activeFilter === 'SHORT_STAY' ? 'brand-primary' : 'gray'"
        size="xs"
        shape="full"
        :disabled="isEmpty && !isEmptyDueToFilter"
        @click="setFilter('SHORT_STAY')"
      >
        <SvgoLuggagesHandbag
          filled
          :font-controlled="false"
          class="size-4 shrink-0 stroke-2"
          aria-hidden="true"
        />
        {{ $t(`${prefix}.filters.shortStay`) }}
      </UiButton>
      <UiButton
        role="radio"
        :aria-checked="activeFilter === 'LONG_STAY'"
        :variant="activeFilter === 'LONG_STAY' ? 'primary' : 'secondary'"
        :color="activeFilter === 'LONG_STAY' ? 'brand-primary' : 'gray'"
        size="xs"
        shape="full"
        :disabled="isEmpty && !isEmptyDueToFilter"
        @click="setFilter('LONG_STAY')"
      >
        <SvgoLuggagesCheckedBag
          filled
          :font-controlled="false"
          class="size-4 shrink-0 stroke-2"
          aria-hidden="true"
        />
        {{ $t(`${prefix}.filters.longStay`) }}
      </UiButton>
    </div>

    <Transition
      mode="out-in"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      leave-active-class="transition-all duration-150 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="status === 'pending'"
        :key="`skeleton-${searchCriteria.originCodeWithDefault}`"
        class="w-full overflow-hidden"
      >
        <div class="-ml-6 flex">
          <div
            v-for="i in 8"
            :key="`skeleton-${i}`"
            class="min-w-0 shrink-0 basis-3/5 pl-6 sm:basis-1/2 lg:basis-1/4"
          >
            <div class="flex flex-col overflow-hidden rounded-xl border bg-card">
              <div
                class="aspect-4/3 w-full animate-shimmer bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
              />
              <div class="flex flex-1 flex-col gap-2 p-4">
                <div
                  class="h-6 w-3/4 animate-shimmer rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
                />
                <div class="flex items-center gap-1.5">
                  <div
                    class="size-3.5 shrink-0 animate-shimmer rounded-sm bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
                  />
                  <div
                    class="h-4 w-1/2 animate-shimmer rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
                  />
                </div>
                <div class="mt-auto flex items-center justify-between pt-2">
                  <div
                    class="h-5 w-20 animate-shimmer rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
                  />
                  <div
                    class="size-8 shrink-0 animate-shimmer rounded-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="visibleFares.length > 0"
        :key="`fares-${searchCriteria.originCodeWithDefault}-${activeFilter}`"
        class="w-full"
      >
        <UiCarousel
          :opts="{ align: 'start', dragFree: false, slidesToScroll: 1 }"
          :plugins="[wheelGesturesPlugin]"
          class="w-full"
          :aria-label="$t(`${prefix}.carouselLabel`)"
          @init-api="onCarouselInit"
        >
          <span
            aria-live="polite"
            aria-atomic="true"
            class="sr-only"
            >{{ $t(`${prefix}.slide`) }} {{ currentSlideIndex + 1 }} / {{ visibleFares.length }}</span
          >
          <UiCarouselContent class="sm:-ml-6">
            <UiCustomCarouselItem
              v-for="(fare, index) in visibleFares"
              :key="fare.cityCode"
              :aria-label="`${$t(`${prefix}.slide`)} ${index + 1} / ${visibleFares.length}`"
              class="min-w-72 max-w-72 basis-3/5 pl-4 sm:basis-2/5 sm:pl-6 lg:basis-1/4"
            >
              <NuxtLink
                :to="localePath(`/results?${fare.searchQuery}`)"
                class="group flex h-full w-full flex-col overflow-hidden rounded-xl bg-card text-left"
                @click="gtmStore.trackLowestFareClicked(fare, activeFilter)"
              >
                <div
                  class="aspect-4/3 w-full animate-shimmer overflow-hidden bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-shimmer"
                >
                  <UiCustomResponsivePicture
                    v-if="fare.imageUrl && !failedPictures.has(fare.cityCode)"
                    :src="fare.imageUrl"
                    sizes="272px"
                    fit="cover"
                    legacy-format="webp"
                    loading="lazy"
                    :alt="locationLabels.city(fare.cityCode)"
                    class="block size-full"
                    :img-attrs="{
                      class:
                        'size-full object-cover object-center transition-transform duration-500 group-hover:scale-105',
                    }"
                    @error="failedPictures.add(fare.cityCode)"
                  />
                  <UiCustomResponsivePicture
                    v-else
                    src="home/hero/default.webp"
                    sizes="272px"
                    fit="cover"
                    legacy-format="webp"
                    loading="lazy"
                    alt=""
                    class="block size-full"
                    :img-attrs="{
                      class:
                        'size-full object-cover object-center transition-transform duration-500 group-hover:scale-105',
                    }"
                  />
                </div>
                <div class="flex flex-1 flex-col gap-4 rounded-b-xl border-x border-b p-4">
                  <div class="flex flex-col gap-2">
                    <span class="text-lg font-semibold text-text-primary-900">
                      {{ locationLabels.city(fare.cityCode) }}
                    </span>
                    <div class="flex items-center gap-1.5 text-xs font-normal text-text-secondary-600">
                      <Calendar
                        class="size-3.5 shrink-0 text-gray-400"
                        aria-hidden="true"
                      />
                      <span>{{
                        formatDateRange(fare.departureDate, fare.returnDate, fullLocale, $t(`${prefix}.dateTo`))
                      }}</span>
                    </div>
                  </div>
                  <div class="flex items-center justify-between rounded-lg bg-brand-primary-50 px-3 py-2">
                    <div class="flex items-baseline gap-1">
                      <span class="text-sm font-normal text-text-secondary-600">{{ $t(`${prefix}.fromPrice`) }}</span>
                      <span class="text-base font-semibold text-brand-primary-700">{{
                        usePriceFormat(fare.price, 1, 0, 0)
                      }}</span>
                    </div>
                    <div
                      class="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-primary-100 text-brand-primary-700"
                    >
                      <ArrowRight
                        class="size-4"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
              </NuxtLink>
            </UiCustomCarouselItem>
          </UiCarouselContent>
          <UiCarouselPrevious class="-left-4 top-32 hidden size-10 border-none shadow-lg disabled:hidden lg:flex">
            <ArrowLeft class="h-4 w-4 text-current" />
            <span class="sr-only">{{ $t(`${prefix}.previousSlide`) }}</span>
          </UiCarouselPrevious>
          <UiCarouselNext class="-right-4 top-32 hidden size-10 border-none shadow-lg disabled:hidden lg:flex">
            <ArrowRight class="h-4 w-4 text-current" />
            <span class="sr-only">{{ $t(`${prefix}.nextSlide`) }}</span>
          </UiCarouselNext>
          <UiCustomCarouselDots class="mt-6" />
        </UiCarousel>
      </div>

      <div
        v-else
        :key="`empty-${searchCriteria.originCodeWithDefault}-${activeFilter}`"
        class="w-full rounded-2xl border border-gray-200 bg-card px-6 py-12 text-center"
      >
        <template v-if="isEmptyDueToFilter">
          <FilterX
            class="mx-auto mb-4 size-8 text-gray-600"
            :stroke-width="1.5"
            aria-hidden="true"
          />
          <p class="text-base font-semibold text-text-primary-900">
            {{ $t(`${prefix}.noFaresFilterTitle`) }}
          </p>
          <p class="mx-auto mt-2 max-w-sm text-sm font-normal text-text-secondary-600">
            {{ $t(`${prefix}.noFaresFilterSubtitle`) }}
          </p>
          <UiButton
            variant="secondary"
            color="gray"
            class="mt-6"
            @click="clearFilter()"
          >
            {{ $t(`${prefix}.noFaresFilterCta`) }}
          </UiButton>
        </template>
        <template v-else>
          <UiCustomCompassIcon class="mx-auto mb-4 size-10 text-gray-600" />
          <p class="text-base font-semibold text-text-primary-900">
            {{ $t(`${prefix}.noFaresTitle`) }}
          </p>
          <p class="mx-auto mt-2 max-w-sm text-sm font-normal text-text-secondary-600">
            {{ $t(`${prefix}.noFaresSubtitle`) }}
          </p>
          <UiButton
            variant="secondary"
            color="gray"
            class="mt-6"
            @click="scrollToOrigin"
          >
            {{ $t(`${prefix}.noFaresCta`) }}
          </UiButton>
        </template>
      </div>
    </Transition>
  </div>
</template>
